import { useEffect, useRef } from "react";
import PropTypes from "prop-types";

// Third Party
import moment from "moment";

// MUI
import { TextField, Tooltip } from "@mui/material";
import { DesktopDateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import DateRangeRoundedIcon from "@mui/icons-material/DateRangeRounded";

// Icons
import { ReactComponent as PencilElement } from "@/assets/icons/svg/Pencil.svg";

const adapter = {
	moment: AdapterMoment,
	fns: AdapterDateFns,
};

const getAlternativeProps = () => {
	return {
		disableUnderline: true,
		paddingRight: 0,
		sx: {
			width: "85%",
			boxShadow: "none",
			color: "#2357BE",
			fontFamily: "PTSansProNarrow",
			fontSize: "14.5px",
			backgroundColor: "transparent",
		},
	};
};

const CustomDateTextField = (params) => {
	return (
		<Tooltip title={params.tooltipMessage} placement={params.tooltipPlacement}>
			<TextField
				className={params.customizedInput ? params.customizedInputClasses : "text-input-error"}
				value={params.value}
				variant={params.variant || "outlined"}
				InputProps={{
					...params?.InputProps,
					...(params.customizedInput ? getAlternativeProps() : {}),
				}}
				inputProps={params.inputProps}
				label={params.label}
				disabled={params.disabled}
				error={params.error}
			/>
		</Tooltip>
	);
};

// Component Documentation https://mui.com/x/api/date-pickers/desktop-date-time-picker/#main-content
export default function DataTableDateTimePicker(props) {
	const inputRef = useRef(null);

	useEffect(() => {
		if (props.useCustomAnchorEl) {
			inputRef.current = props.customAnchorEl;
		}
	}, [props.apiRef]);

	const getPopperExtraProps = () => {
		if (props.useCustomAnchorEl) {
			const horizontalOffset = 400;
			return {
				anchorEl: inputRef.current,
				modifiers: props.useCustomAnchorEl
					? [
							{
								name: "offset",
								options: {
									offset: [horizontalOffset, 0], // Adjust the horizontal and vertical offsets
								},
							},
					  ]
					: [],
			};
		}
		return {
			popperOptions: {
				placement: "bottom",
			},
		};
	};

	return (
		<LocalizationProvider dateAdapter={adapter[props.adapter || "moment"]}>
			<DesktopDateTimePicker
				format={props.format}
				maxDateTime={moment(props.maxDateTime)}
				maxTime={moment(props.maxTime)}
				maxDate={moment(props.maxDate)}
				minDate={moment(props.minDate)}
				minDateTime={moment(props.minDateTime)}
				minTime={moment(props.minTime)}
				value={moment(props.value)}
				slots={{
					openPickerIcon: !props.customizedInput ? DateRangeRoundedIcon : PencilElement,
					textField: CustomDateTextField,
				}}
				slotProps={{
					toolbar: {
						hidden: !props.showToolbar,
						toolbarFormat: props.toolbarFormat,
					},
					popper: {
						className: "custom-date-time-picker table-custom-date-time-picker",
						...getPopperExtraProps(),
					},
					tabs: {
						hidden: props.hideTabs,
					},
					actionBar: {
						actions: props.actions,
					},
					textField: {
						customizedInput: props.customizedInput,
						customizedInputClasses: props.customizedInputClasses,
						tooltipMessage: props.tooltipMessage,
						tooltipPlacement: props.tooltipPlacement,
						variant: props.variant,
						error: props.error,
					},
				}}
				viewRenderers={props.viewRenderers}
				closeOnSelect={true}
				views={props.views}
				ampm={props.ampm}
				disableIgnoringDatePartForTimeValidation={props.disableIgnoringDatePartForTimeValidation}
				mask={props.mask}
				disableMaskedInput
				disabled={props.disabled}
				disableHighlightToday={props.disableHighlightToday}
				disableFuture={props.disableFuture}
				disablePast={props.disablePast}
				minutesStep={props.minutesStep}
				referenceDate={props.defaultCalendarMonth}
				readOnly={props.readOnly}
				shouldDisableDate={props.shouldDisableDate}
				shouldDisableMonth={props.shouldDisableMonth}
				shouldDisableTime={props.shouldDisableTime}
				shouldDisableYear={props.shouldDisableYear}
				onChange={props.onChange}
				onOpen={props.onOpen}
				onClose={props.onClose}
				onError={props.onError}
			/>
		</LocalizationProvider>
	);
}

DataTableDateTimePicker.propTypes = {
	views: PropTypes.array,
	ampm: PropTypes.bool,
	adapter: PropTypes.oneOf(["moment", "fns"]),
	showToolbar: PropTypes.bool,
	shouldDisableDate: PropTypes.func,
	shouldDisableMonth: PropTypes.func,
	shouldDisableTime: PropTypes.func,
	shouldDisableYear: PropTypes.func,
	onChange: PropTypes.func,
	onClose: PropTypes.func,
	onError: PropTypes.func,
	customizedInput: PropTypes.bool,
	readOnly: PropTypes.bool,
	format: PropTypes.string,
	maxDateTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
	maxTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
	maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
	minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
	minDateTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
	minTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
	value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
	mask: PropTypes.array,
	disabled: PropTypes.bool,
	disableIgnoringDatePartForTimeValidation: PropTypes.bool,
	disableHighlightToday: PropTypes.bool,
	disableFuture: PropTypes.bool,
	disablePast: PropTypes.bool,
	minutesStep: PropTypes.number,
	defaultCalendarMonth: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
	toolbarFormat: PropTypes.string,
	onOpen: PropTypes.func,
	hideTabs: PropTypes.bool,
	error: PropTypes.bool,
	viewRenderers: PropTypes.object,
	actions: PropTypes.array,
	tooltipMessage: PropTypes.string,
	tooltipPlacement: PropTypes.string,
	useCustomAnchorEl: PropTypes.bool,
	apiRef: PropTypes.object,
	variant: PropTypes.string,
	customAnchorEl: PropTypes.object,
	customizedInputClasses: PropTypes.string,
};
